import React, { useState } from 'react';
import { Building, User, CreditCard, Check, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { buildApiUrl } from '../config/api';

interface OnboardingProps {
  email: string;
  onComplete: (token: string, user: any) => void;
}

interface PlanPricing {
  monthly: number;
  annual: number;
  annualMonthly: number; // effective monthly when billed annually
}

interface Plan {
  id: string;
  name: string;
  pricing: PlanPricing;
  description: string;
  features: string[];
  highlight?: string;
}

type BillingCycle = 'monthly' | 'annual';

export default function Onboarding({ email, onComplete }: OnboardingProps) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string>('pro');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameManuallySet, setNameManuallySet] = useState(false);

  const plans: Plan[] = [
    {
      id: 'pro',
      name: 'Pro',
      pricing: {
        monthly: 299,
        annual: 2990,
        annualMonthly: 249
      },
      description: 'Perfect for growing teams',
      features: [
        '2 projects',
        '1M events/month',
        '30-day data retention',
        'Journeys & Topic clustering',
        'Email support'
      ],
      highlight: 'Most Popular'
    },
    {
      id: 'business',
      name: 'Business', 
      pricing: {
        monthly: 799,
        annual: 7990,
        annualMonthly: 666
      },
      description: 'For scaling organizations',
      features: [
        '10 projects',
        '10M events/month',
        '90-day data retention',
        'Read-only API access',
        'Priority support'
      ]
    }
  ];

  const TRIAL_DAYS = 14;

  // Pre-fill organization name from email domain
  React.useEffect(() => {
    if (email && !organizationName) {
      const domain = email.split('@')[1];
      if (domain && domain !== 'gmail.com' && domain !== 'yahoo.com' && domain !== 'hotmail.com' && domain !== 'outlook.com') {
        const orgName = domain.split('.')[0];
        setOrganizationName(orgName.charAt(0).toUpperCase() + orgName.slice(1));
      }
    }
  }, [email, organizationName]);

  // Pre-fill name from email
  React.useEffect(() => {
    if (email && !name && !nameManuallySet) {
      const emailPart = email.split('@')[0];
      const formattedName = emailPart
        .replace(/[._]/g, ' ')
        .replace(/\w\S*/g, (txt: string) => 
          txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
      setName(formattedName);
    }
  }, [email, name, nameManuallySet]);

  const handleNext = () => {
    if (step === 1 && name.trim()) {
      setStep(2);
    } else if (step === 2 && organizationName.trim()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleStartTrial = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('api/v1/onboarding/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          name: name.trim(),
          organization_name: organizationName.trim(),
          plan: selectedPlan,
          billing_cycle: billingCycle
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start checkout');
      }

      const data = await response.json();
      window.location.href = data.data.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoading(false);
    }
  };

  const getDisplayPrice = (plan: Plan) => {
    if (billingCycle === 'annual') {
      return plan.pricing.annualMonthly;
    }
    return plan.pricing.monthly;
  };

  const getAnnualSavings = (plan: Plan) => {
    const monthlyCost = plan.pricing.monthly * 12;
    const annualCost = plan.pricing.annual;
    return monthlyCost - annualCost;
  };

  const renderStep1 = () => (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <User className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Welcome to Turret!</CardTitle>
        <CardDescription>
          Let's get your account set up
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              disabled
              className="bg-muted"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="name">Your Full Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameManuallySet(true);
              }}
              disabled={loading}
            />
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-sm text-red-600">{error}</div>
          </div>
        )}
        
        <Button 
          onClick={handleNext}
          disabled={!name.trim() || loading}
          className="w-full"
        >
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Building className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Create Your Organization</CardTitle>
        <CardDescription>
          Choose a name for your organization
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="organization">Organization Name</Label>
            <Input
              id="organization"
              type="text"
              placeholder="Acme Corp"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              You can change this later in settings
            </p>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-sm text-red-600">{error}</div>
          </div>
        )}
        
        <div className="flex space-x-3">
          <Button 
            variant="outline"
            onClick={handleBack}
            disabled={loading}
            className="flex-1"
          >
            Back
          </Button>
          <Button 
            onClick={handleNext}
            disabled={!organizationName.trim() || loading}
            className="flex-1"
          >
            Continue
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card className="w-full max-w-3xl">
      <CardHeader className="text-center pb-2">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <CreditCard className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Choose Your Plan</CardTitle>
        <CardDescription className="text-base">
          Start with a {TRIAL_DAYS}-day free trial. Cancel anytime.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Billing Cycle Toggle */}
        <div className="flex flex-col items-center space-y-3">
          <div className="inline-flex items-center bg-muted p-1 rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-white shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <Badge className="bg-green-500 hover:bg-green-500 text-white text-[10px] px-1.5 py-0">
                2 MONTHS FREE
              </Badge>
            </button>
          </div>
          
          {billingCycle === 'annual' && (
            <div className="flex items-center gap-2 text-green-600 text-sm font-medium animate-pulse">
              <Sparkles className="h-4 w-4" />
              <span>You're saving 2 months per year!</span>
              <Sparkles className="h-4 w-4" />
            </div>
          )}
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const displayPrice = getDisplayPrice(plan);
            const annualSavings = getAnnualSavings(plan);
            
            return (
              <div
                key={plan.id}
                className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-lg'
                    : 'border-border hover:border-primary/50 hover:shadow-md'
                }`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
                
                {/* Popular badge */}
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-white px-3 shadow-md">
                      <Zap className="h-3 w-3 mr-1" />
                      {plan.highlight}
                    </Badge>
                  </div>
                )}
                
                <div className="space-y-4 pt-2">
                  {/* Plan name and description */}
                  <div>
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {plan.description}
                    </p>
                  </div>
                  
                  {/* Pricing */}
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">${displayPrice}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    
                    {billingCycle === 'annual' ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground line-through">
                            ${plan.pricing.monthly}/mo
                          </span>
                          <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                            Save ${annualSavings}/yr
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Billed as ${plan.pricing.annual.toLocaleString()}/year
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Billed monthly
                      </p>
                    )}
                  </div>
                  
                  {/* Features */}
                  <div className="space-y-2 pt-2 border-t">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-primary">{TRIAL_DAYS}-day free trial</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Annual savings callout */}
        {billingCycle === 'monthly' && (
          <div 
            className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setBillingCycle('annual')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Switch to annual billing</p>
                  <p className="text-sm text-green-700">
                    Save ${selectedPlan === 'pro' ? '598' : '1,598'} per year (2 months free!)
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="border-green-300 text-green-700 hover:bg-green-100">
                Switch
              </Button>
            </div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-sm text-red-600">{error}</div>
          </div>
        )}

        {/* Credit card notice */}
        <p className="text-xs text-center text-muted-foreground">
          Credit card required to start trial. You won't be charged until your trial ends.
        </p>
        
        <div className="flex space-x-3">
          <Button 
            variant="outline"
            onClick={handleBack}
            disabled={loading}
            className="flex-1"
          >
            Back
          </Button>
          <Button 
            onClick={handleStartTrial}
            disabled={!selectedPlan || loading}
            className="flex-1 bg-primary hover:bg-primary/90"
          >
            {loading ? 'Starting...' : `Start ${TRIAL_DAYS}-Day Free Trial`}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Progress indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center space-x-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= stepNum
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {stepNum}
                </div>
                {stepNum < 3 && (
                  <div
                    className={`w-12 h-1 ${
                      step > stepNum ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex justify-center">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );
}
